
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Recycle } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatAluminium = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Aluminium - Rachat Profilés, Tôles, Jantes | Groupe Fabre"
        description="Rachat d'aluminium au meilleur prix : profilés, tôles, jantes, canettes. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/aluminium"
        keywords="achat aluminium, rachat jantes alu, profilés aluminium, prix aluminium, recyclage aluminium"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-400 to-gray-500 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Recycle className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat d'Aluminium</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de profilés, tôles, jantes et canettes</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat d'aluminium - Métal léger valorisé
                </h2>
                <p className="text-gray-600 mb-6">
                  L'<strong>aluminium</strong> est un métal non ferreux très recherché pour le recyclage. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types d'aluminium : 
                  profilés de menuiserie, tôles, jantes auto et déchets d'emballage.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types d'aluminium rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Profilés :</strong> menuiserie, construction, industrie</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Tôles :</strong> carrosserie, bardage, plaques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Jantes :</strong> automobiles, motos, vélos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Emballages :</strong> canettes, barquettes, films</span>
                  </li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                  <h4 className="font-bold text-blue-800 mb-3">Avantages du recyclage aluminium</h4>
                  <ul className="text-blue-700 text-sm space-y-2">
                    <li>• Recyclage infini sans perte de qualité</li>
                    <li>• Économie d'énergie de 95% vs production primaire</li>
                    <li>• Forte valeur marchande</li>
                    <li>• Impact environnemental positif</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Valorisez vos déchets alu</h3>
                <p className="text-gray-600 mb-6">
                  L'aluminium conserve ses propriétés lors du recyclage. 
                  Apportez vos déchets d'aluminium sur nos sites pour une valorisation optimale.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Prix attractifs aluminium</span>
                  </div>
                  <div className="flex items-center">
                    <Recycle className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Recyclage 100% écologique</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Tri par qualité d'alliage</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition-colors">
                    Demander prix aluminium
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Types d'aluminium */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications aluminium et tarifs
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Alu Propre</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Profilés, plaques, sans fer ni autres matériaux
                </p>
                <div className="text-green-600 font-medium">Prix le plus élevé</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Alu Mélangé</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Mélange de différents alliages d'aluminium
                </p>
                <div className="text-orange-600 font-medium">Prix intermédiaire</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Alu Radiateur</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Radiateurs auto, échangeurs thermiques
                </p>
                <div className="text-blue-600 font-medium">Prix spécifique</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatAluminium;
